import _merge from "lodash/merge";
import PatternFill from 'highcharts/modules/pattern-fill';
import XRange from 'highcharts/modules/xrange';
import Highcharts, { Point } from 'highcharts';
import { getDimensions } from '@kentik/ui-shared/query/query';
import { formatRowName, getBaseChartStyle } from 'app/views/ude/dataviews/util/highcharts.util';
import { getMeasurementMetric } from '@kentik/ui-shared/query/measurements';
import { getEnumHealthyValue } from '@kentik/ui-shared/query/enumUtils';
import { getTimezone } from 'core/util/timezone';

// TODO these could be more efficiently loaded on demand
PatternFill(Highcharts);
XRange(Highcharts);

// Declaration merging to add in all the custom properties we may be using for various tooltip configs.

/**
 * Fuzz tolerance for blocks to be considered "contiguous" even if there are time discrepancies.
 */
const MERGE_DISTANCE = 1000;

// height (in pixels) to dedicate to each timeline row
const TIMELINE_ROW_HEIGHT = 50;
// can be determined with CSS inspection - this is the size of the graph minus the size of the data
const TIMELINE_HEADER_HEIGHT = 82;
// extra height reserved for the state color legend rendered below the chart
const TIMELINE_LEGEND_HEIGHT = 50;
function timelinePointFormatter() {
  const val = this.custom?.formatted ?? this.custom?.stateValue;
  const startX = this.x;
  const endX = this.x2 ?? this.x;
  const {
    time
  } = this.series.chart;
  const start = time.dateFormat('%H:%M:%S', startX);
  const end = time.dateFormat('%H:%M:%S', endX);
  const durationMs = endX - startX;
  const durationMins = (durationMs / 60000).toFixed(0);
  return `
          <span style="font-size: 10px; font-weight: bold">${this.series.yAxis.categories[this.y || 0]}</span><br/>
          State: <b style="color:${this.custom?.tooltipColor || this.color}">${val}</b><br/>
          Time: <b>${start}</b> - <b>${end}</b> (${durationMins} min)
        `;
}
/**
 * Build a stable map of enum value -> color for the metric. When a healthy
 * value is declared, the healthy state takes the green stateTimeline color and
 * remaining states are assigned distinct hues from the danger palette. When no
 * healthy value is declared, all states get neutral categorical hues.
 */
function buildValueColorMap({
  values,
  healthyValue,
  themeColors
}) {
  const dangerPalette = themeColors.categoricalDanger ?? ['#dc3545'];
  const neutralPalette = themeColors.categoricalNeutral ?? ['#3b82f6'];
  const healthyColor = themeColors.healthy?.backgroundHover ?? '#28a745';
  const out = {};
  const sortedKeys = Object.keys(values).sort((a, b) => Number(a) - Number(b));
  if (healthyValue !== undefined) {
    let dangerIdx = 0;
    const healthyKey = String(healthyValue);
    sortedKeys.forEach(key => {
      if (key === healthyKey) {
        out[key] = healthyColor;
      } else {
        out[key] = dangerPalette[dangerIdx % dangerPalette.length];
        dangerIdx += 1;
      }
    });
  } else {
    sortedKeys.forEach((key, idx) => {
      out[key] = neutralPalette[idx % neutralPalette.length];
    });
  }
  return out;
}
export function generateTimelineOptions({
  queryModel,
  theme,
  metricName,
  measurement,
  dimensionFormatter
}) {
  const {
    results
  } = queryModel;
  const dimensions = getDimensions(queryModel.query);
  const measurementMetric = getMeasurementMetric(measurement, metricName);
  const schemaValues = measurementMetric?.Values;
  const healthyValue = getEnumHealthyValue(measurementMetric, metricName);

  // Collect all distinct raw value keys observed in the data so we can build
  // a fallback label map when the schema Values is absent (transitional backends
  // that haven't yet returned enum values in the NMS API response).
  const observedKeys = new Set();
  results.rows.forEach(row => {
    const rowVals = row.values[metricName]?.values;
    if (Array.isArray(rowVals)) {
      rowVals.forEach(v => {
        if (v != null) {
          observedKeys.add(String(v));
        }
      });
    }
  });

  // Build label map: prefer schema Values; fall back to raw value strings for
  // observed keys when schema is absent (avoids "Unidentified State X" labels).
  const valueToLabel = schemaValues && Object.keys(schemaValues).length > 0 ? Object.fromEntries(Object.entries(schemaValues)) : Object.fromEntries(Array.from(observedKeys).map(k => [k, String(k)]));
  const themeColors = theme.colors.stateTimeline ?? {};
  const unknownColor = themeColors.unknown?.backgroundHover ?? '#d0d0d0';
  const valueToColor = buildValueColorMap({
    values: valueToLabel,
    healthyValue,
    themeColors
  });

  // Diagonal stripe pattern used for the "Unknown" (null-valued) state, per
  // product spec - this distinguishes a missing data point from a real state.
  const unknownPattern = {
    pattern: {
      path: {
        d: 'M 0 10 L 10 0 M -1 1 L 1 -1 M 9 11 L 11 9',
        strokeWidth: 2,
        stroke: unknownColor
      },
      width: 10,
      height: 10,
      backgroundColor: 'transparent'
    }
  };

  // Use a Symbol as the map key for null so we cannot collide with a real
  // string value of "__null__" that some metrics may legitimately emit.
  const NULL_KEY = Symbol('null-state');
  // Get all rows matching the metricName
  const validRows = results.rows.filter(row => row.values[metricName]);
  const categories = validRows.map(row => formatRowName(row.get(), dimensions, dimensionFormatter));

  // Build per-state data: stateKey → { label, color, points[] }
  // One Highcharts series per state gives us a native legend for free.
  // Using reduce here makes it explicit that stateMap is the direct functional
  // product of folding over validRows.

  const stateMap = validRows.reduce((acc, row, yIndex) => {
    const {
      values
    } = row.values[metricName];
    const {
      timestamps
    } = row;
    if (!values.length || !timestamps.length) {
      return acc;
    }

    // Determine interval size based on data received - we assume/hope that intervals are consistent!
    let interval = 0;
    if (timestamps.length > 1) {
      interval = (timestamps[1] - timestamps[0]) * 1000;
    }
    let currentBlock = null;
    let currentStateKey = null;

    // Convert all data points into a list of intervals
    for (let i = 0; i < values.length; i++) {
      const rawValue = values[i];
      const isNull = rawValue == null;
      const stateKey = isNull ? NULL_KEY : String(rawValue);
      const startTime = timestamps[i] * 1000;

      // Calculate end time
      let endTime;
      if (i < timestamps.length - 1) {
        endTime = timestamps[i + 1] * 1000;
      } else {
        // Last point - extend by one interval
        endTime = startTime + interval;
      }

      // Look up label & color from the metric's Values map
      const label = isNull ? 'Unknown' : valueToLabel[String(rawValue)] ?? `Unidentified State ${rawValue}`;
      const color = isNull ? unknownPattern : valueToColor[String(rawValue)] ?? unknownColor;
      if (!acc.has(stateKey)) {
        acc.set(stateKey, {
          label,
          color,
          points: []
        });
      }

      // Standard interval-merge algo
      if (currentBlock && currentStateKey === stateKey && currentBlock.x2 !== undefined && Math.abs(startTime - currentBlock.x2) < MERGE_DISTANCE) {
        // Extend current block if we have a nearby match
        currentBlock.x2 = endTime;
      } else {
        // Otherwise close out the previous block (if any)...
        if (currentBlock && currentStateKey !== null) {
          acc.get(currentStateKey)?.points.push(currentBlock);
        }
        // ...and start a new one.
        currentStateKey = stateKey;
        currentBlock = {
          x: startTime,
          x2: endTime,
          y: yIndex,
          custom: {
            stateValue: isNull ? null : rawValue,
            formatted: label
          }
        };
      }
    }

    // Push the trailing block from this row
    if (currentBlock && currentStateKey !== null) {
      acc.get(currentStateKey)?.points.push(currentBlock);
    }
    return acc;
  }, new Map());

  // Sort: healthy first, then ascending numeric key, unknown (null) last
  const healthyKey = healthyValue !== undefined ? String(healthyValue) : undefined;
  const sortedStateKeys = Array.from(stateMap.keys()).sort((a, b) => {
    if (a === NULL_KEY) {
      return 1;
    }
    if (b === NULL_KEY) {
      return -1;
    }
    if (healthyKey !== undefined && a === healthyKey) {
      return -1;
    }
    if (healthyKey !== undefined && b === healthyKey) {
      return 1;
    }
    return Number(a) - Number(b);
  });
  const seriesArray = sortedStateKeys.map(stateKey => {
    const entry = stateMap.get(stateKey);
    const fallbackLabel = stateKey === NULL_KEY ? 'Unknown' : String(stateKey);
    const {
      label,
      color,
      points
    } = entry ?? {
      label: fallbackLabel,
      color: unknownColor,
      points: []
    };
    return {
      type: 'xrange',
      name: label,
      color,
      colorByPoint: false,
      // grouping: false ensures bars from different series share the same row
      // height rather than being stacked side-by-side within a category.
      grouping: false,
      showInLegend: true,
      data: points,
      dataLabels: {
        enabled: false
      },
      borderWidth: 0,
      pointPadding: 0.05,
      groupPadding: 0.22
    };
  });
  const options = {
    chart: {
      type: 'xrange',
      // TimelineView needs to be a fixed height as Highcharts does not expect labels to be above the data,
      // so standard responsive styling does not work here.
      height: validRows.length * TIMELINE_ROW_HEIGHT + TIMELINE_HEADER_HEIGHT + TIMELINE_LEGEND_HEIGHT,
      marginTop: 17
    },
    time: {
      timezone: getTimezone(queryModel.settings.chart?.xAxis?.timezone || 'UTC')
    },
    title: {
      text: undefined
    },
    legend: {
      enabled: true,
      layout: 'horizontal',
      align: 'left',
      verticalAlign: 'bottom',
      itemStyle: {
        color: theme.colors.body,
        fontWeight: 'normal',
        fontSize: '12px'
      },
      symbolRadius: 2
    },
    xAxis: {
      type: 'datetime',
      crosshair: true
    },
    yAxis: {
      type: 'category',
      title: {
        text: undefined
      },
      categories,
      reversed: true,
      gridLineWidth: 0,
      labels: {
        // labels sit slightly above the data, rather than off to the side (so they do not take up graph space)
        align: 'left',
        reserveSpace: false,
        x: 5,
        y: -18,
        style: {
          fontSize: '12px',
          color: theme.colors.body
        }
      }
    },
    tooltip: {
      shared: false,
      followPointer: true,
      useHTML: true,
      headerFormat: '',
      pointFormatter: timelinePointFormatter
    },
    series: seriesArray
  };

  // TODO seriesRowIds??
  return {
    options: _merge({}, getBaseChartStyle(theme), options),
    seriesRowIds: []
  };
}